import React, { useContext } from 'react'
import { Button } from 'react-bootstrap'
import { ThemeContext } from '../components/ThemeContext.js'

const endpoint = 'https://api.geraniumpos.com/api/uploads'

const apiExample = `import axios from 'axios'
import FormData from 'form-data'
import fs from 'fs'

const qfname = 'receipt-1001.png'
const cid = 'YOUR_CUSTOMER_ID'
const qrid = 'USER_QR_ID'
const barcode = '1234567890123'

const formData = new FormData()
formData.append('file', fs.createReadStream('./receipt.png'), {
  filename: qfname,
  contentType: 'image/png'
})

const url = '${endpoint}?qfname=' + encodeURIComponent(qfname)
  + '&cid=' + encodeURIComponent(cid)
  + '&qrid=' + encodeURIComponent(qrid)
  + '&barcode=' + encodeURIComponent(barcode)

const response = await axios.put(url, formData, {
  headers: {
    ...formData.getHeaders(),
    Authorization: 'Bearer YOUR_API_KEY'
  },
  timeout: 20000
})

console.log(response.status, response.data)`

const successResponse = `{
  "success": true,
  "message": "Receipt uploaded",
  "receiptId": "67fd12ab34cd56ef78901234"
}`

const errorResponse = `{
  "success": false,
  "message": "Invalid API key"
}`

function ApiDocument() {
  const { theme } = useContext(ThemeContext)

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Ignore clipboard failures silently to avoid UI noise.
    }
  }

  return (
    <div
      style={{
        padding: '1.25rem',
        maxWidth: 800,
        margin: '0 auto',
        color: theme.text
      }}
    >
      <h2 style={{ marginBottom: '0.25rem' }}>API Connection Instructions</h2>
      <p style={{ color: theme.subtext, marginTop: 0 }}>
        Version v1 | Updated April 2026
      </p>

      <div
        style={{
          backgroundColor: theme.card,
          border: `1px solid ${theme.sidebarAccent}`,
          borderRadius: '8px',
          padding: '0.9rem',
          marginBottom: '1rem'
        }}
      >
        <p style={{ marginBottom: '0.6rem' }}>
          <strong>Endpoint:</strong> <code>{endpoint}</code>
        </p>
        <Button
          size='sm'
          variant='outline-primary'
          onClick={() => copyText(endpoint)}
          style={{
            borderColor: theme.accent,
            color: theme.accent,
            backgroundColor: 'transparent'
          }}
        >
          Copy Endpoint
        </Button>
      </div>

      <h3>Request Parameters</h3>
      <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  borderBottom: `1px solid ${theme.sidebarAccent}`,
                  padding: '0.5rem'
                }}
              >
                Name
              </th>
              <th
                style={{
                  textAlign: 'left',
                  borderBottom: `1px solid ${theme.sidebarAccent}`,
                  padding: '0.5rem'
                }}
              >
                Required
              </th>
              <th
                style={{
                  textAlign: 'left',
                  borderBottom: `1px solid ${theme.sidebarAccent}`,
                  padding: '0.5rem'
                }}
              >
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '0.5rem' }}>
                <code>qfname</code>
              </td>
              <td style={{ padding: '0.5rem' }}>Yes</td>
              <td style={{ padding: '0.5rem' }}>Uploaded receipt filename.</td>
            </tr>
            <tr>
              <td style={{ padding: '0.5rem' }}>
                <code>cid</code>
              </td>
              <td style={{ padding: '0.5rem' }}>Yes</td>
              <td style={{ padding: '0.5rem' }}>Business customer ID.</td>
            </tr>
            <tr>
              <td style={{ padding: '0.5rem' }}>
                <code>qrid</code>
              </td>
              <td style={{ padding: '0.5rem' }}>Yes</td>
              <td style={{ padding: '0.5rem' }}>User QR code ID from scan.</td>
            </tr>
            <tr>
              <td style={{ padding: '0.5rem' }}>
                <code>barcode</code>
              </td>
              <td style={{ padding: '0.5rem' }}>Yes</td>
              <td style={{ padding: '0.5rem' }}>Receipt barcode value.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Headers</h3>
      <ul>
        <li>
          <code>Authorization: Bearer YOUR_API_KEY</code>
        </li>
        <li>
          <code>Content-Type: multipart/form-data</code>
        </li>
      </ul>

      <h3>Example Request</h3>
      <pre
        style={{
          background: theme.surfaceMuted,
          color: theme.text,
          padding: '1rem',
          borderRadius: '8px',
          border: `1px solid ${theme.sidebarAccent}`,
          lineHeight: 1.5,
          overflowX: 'auto'
        }}
      >
        <code>{apiExample}</code>
      </pre>
      <Button
        size='sm'
        variant='outline-primary'
        onClick={() => copyText(apiExample)}
        style={{
          borderColor: theme.accent,
          color: theme.accent,
          backgroundColor: 'transparent',
          marginBottom: '1rem'
        }}
      >
        Copy Example
      </Button>

      <h3>Sample Responses</h3>
      <p style={{ marginBottom: '0.35rem' }}>
        <strong>200 Success</strong>
      </p>
      <pre
        style={{
          background: theme.surfaceMuted,
          color: theme.text,
          padding: '0.9rem',
          borderRadius: '8px',
          border: `1px solid ${theme.sidebarAccent}`,
          overflowX: 'auto'
        }}
      >
        <code>{successResponse}</code>
      </pre>
      <p style={{ marginBottom: '0.35rem' }}>
        <strong>401/400 Error</strong>
      </p>
      <pre
        style={{
          background: theme.surfaceMuted,
          color: theme.text,
          padding: '0.9rem',
          borderRadius: '8px',
          border: `1px solid ${theme.sidebarAccent}`,
          overflowX: 'auto'
        }}
      >
        <code>{errorResponse}</code>
      </pre>

      <h3>Security Notes</h3>
      <ul>
        <li>Do not expose API keys in browser-only client apps.</li>
        <li>Prefer server-side API calls when possible.</li>
        <li>Use timeouts and retry policies for network reliability.</li>
      </ul>

      <p>
        Replace <code>YOUR_API_KEY</code> with your actual API key.
      </p>
    </div>
  )
}

export default ApiDocument
